import React from "react";

import cx from "classnames";
import { select, pointer } from "d3-selection";

import { ChartDataPoint, useChartContext } from "./ChartContext";
import { ChartTooltip, tooltipCoords } from "./ChartTooltip";
import { chartWidth, translateTo, xBarHelper, yBarHelper } from "./helpers";
import styles from "./StandardBarChart.scss";

export function Bars() {
  const { svgRef, data, margin, dimensions, tooltipContent } =
    useChartContext();

  const [selectedData, setSelectedData] = React.useState(data[0]);
  const [showTooltip, setShowTooltip] = React.useState(false);

  const chartBarStyle = cx("chart-bars-container", styles["chart-bars"]);

  React.useEffect(() => {
    const svgElement = select(svgRef.current);
    const svgWidth = parseInt(svgElement.style("width"), 10);
    const svgHeight = parseInt(svgElement.style("height"), 10);

    const width = chartWidth(svgWidth, margin);

    const xBar = xBarHelper(data, margin, width);
    const yBar = yBarHelper(data, margin, svgHeight);

    svgElement
      .select(".chart-bars-container")
      .selectAll("rect")
      .data(data)
      .join("rect")
      .attr("x", (d, i) => xBar(String(i)) || 0)
      .attr("y", (d) => yBar(d.value))
      .attr("height", (d) => yBar(0) - yBar(d.value))
      .attr("width", xBar.bandwidth())
      .attr("class", (d) => cx(d.className, chartBarStyle))
      .on("mouseenter", mouseEnter)
      .on("mousemove", mouseMove)
      .on("mouseout", mouseOut);

    function mouseEnter(event: unknown, d: ChartDataPoint) {
      setSelectedData(d);
      setShowTooltip(true);
    }

    // istanbul ignore next - requires an actual screen to test
    function mouseMove(event: { current: unknown }) {
      const svgElement = select(svgRef.current);
      setShowTooltip(true);
      const [xPoint, yPoint] = tooltipCoords(
        svgRef,
        pointer(event, event.current)
      );

      svgElement
        .select(".chart-tooltip")
        .attr("transform", translateTo(xPoint, yPoint));
    }

    function mouseOut() {
      setShowTooltip(false);
    }
  }, [dimensions]);

  return (
    <>
      <g className="chart-bars-container" data-testid="chart-bars" />
      {tooltipContent ? (
        <ChartTooltip
          svgRef={svgRef}
          tooltipContent={tooltipContent}
          data={selectedData}
          showTooltip={showTooltip}
        />
      ) : null}
    </>
  );
}
